import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { nanoid } from 'nanoid';

export interface IMessage {
  message: string;
  id?: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
}

export interface IMessagesContextState {
  messages: IMessage[];
}

export type MessagesContextValue = [IMessagesContextState, Dispatch<SetStateAction<IMessagesContextState>>];

const initialState: IMessagesContextState = {
  messages: [],
};

export const MessagesContext = React.createContext<MessagesContextValue | undefined>(undefined);

export const MessagesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const context = useState<IMessagesContextState>(initialState);

  return <MessagesContext.Provider value={context}>{children}</MessagesContext.Provider>;
};

export const useMessages = () => {
  const context = useContext(MessagesContext);

  if (!context) {
    throw new Error('useMessages must be used within a MessagesContext');
  }

  const [state, setState] = context;

  const add = (message: IMessage) => {
    if (!message.id) {
      message.id = nanoid();
    }
    state.messages.push(message);
    setState({ ...state, messages: [...state.messages] });
  };

  const remove = (id: string) => {
    const messageIndex = state.messages.findIndex(message => message.id === id);
    if (messageIndex > -1) {
      state.messages.splice(messageIndex, 1);
      setState({
        ...state,
        messages: [...state.messages],
      });
    }
  };

  const clear = React.useCallback(() => setState({ messages: [] }), [setState]);

  return {
    ...state,
    add,
    remove,
    clear,
  };
};
